/**
 * Kitchen brand selection — which APPROVED brand this kitchen buys for an
 * ingredient ("we serve Brakebush chicken nuggets").
 *
 * Distinct from `substitution-requests.ts`: every live brand option is already
 * a chef-approved substitution (chefs cannot add brands), so choosing among
 * them applies immediately with no approval. Swapping in a genuinely different
 * ingredient still goes through the substitution request flow.
 *
 * Server primitive is `KitchenBrandSelectionsManager`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiFetch } from './http'

export interface KitchenBrandOption {
  id: string
  /** Full identifying label: brand — product name — size — package. */
  label: string
  /** Primary row line: brand → manufacturer → the option's own name. */
  title: string
  /** Identifying facts beyond the title (product name, size, package),
   *  or null when the title already says it all. */
  detail: string | null
  name: string
  brandName: string | null
  isDefault: boolean
}

export interface KitchenBrandSelection {
  id: string
  ingredientId: string
  ingredientName: string
  brandOptionId: string
  brandOptionLabel: string
  /** YYYY-MM-DD, or null for open-ended. */
  startDate: string | null
  endDate: string | null
}

export const kitchenBrandSelectionsQueryKey = (kitchenLocationId: string) =>
  ['kitchen-brand-selections', kitchenLocationId] as const

export const kitchenBrandOptionsQueryKey = (kitchenLocationId: string, ingredientId: string) =>
  ['kitchen-brand-selections', 'options', kitchenLocationId, ingredientId] as const

export function useKitchenBrandSelectionsQuery(kitchenLocationId: string | null, opts: { enabled?: boolean } = {}) {
  return useQuery<{ selections: KitchenBrandSelection[] }>({
    queryKey: kitchenBrandSelectionsQueryKey(kitchenLocationId ?? ''),
    queryFn: ({ signal }) =>
      apiFetch<{ selections: KitchenBrandSelection[] }>('/api/kitchen-brand-selections', {
        query: { kitchenLocationId: kitchenLocationId ?? '' },
        signal,
      }),
    enabled: (opts.enabled ?? true) && Boolean(kitchenLocationId),
    staleTime: 30_000,
  })
}

/** The brands a chef may pick for one ingredient. Fetched on demand — the
 *  picker is opened rarely, so this stays off the orders payload. */
export function useKitchenBrandOptionsQuery(
  kitchenLocationId: string | null,
  ingredientId: string | null,
  opts: { enabled?: boolean } = {},
) {
  return useQuery<{ options: KitchenBrandOption[] }>({
    queryKey: kitchenBrandOptionsQueryKey(kitchenLocationId ?? '', ingredientId ?? ''),
    queryFn: ({ signal }) =>
      apiFetch<{ options: KitchenBrandOption[] }>('/api/kitchen-brand-selections/options', {
        query: { kitchenLocationId: kitchenLocationId ?? '', ingredientId: ingredientId ?? '' },
        signal,
      }),
    enabled: (opts.enabled ?? true) && Boolean(kitchenLocationId) && Boolean(ingredientId),
    staleTime: 60_000,
  })
}

export interface SelectKitchenBrandInput {
  kitchenLocationId: string
  ingredientId: string
  brandOptionId: string
  /** YYYY-MM-DD; omit for open-ended ("from now on"). */
  startDate?: string | null
  endDate?: string | null
}

export function useSelectKitchenBrandMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: SelectKitchenBrandInput) =>
      apiFetch<{ selection: KitchenBrandSelection }>('/api/kitchen-brand-selections', {
        method: 'PUT',
        body: input,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kitchen-brand-selections'] })
      // The resolved brand is baked into the orders payload.
      void qc.invalidateQueries({ queryKey: ['kitchen-orders', 'shipments'] })
      // ...and into the shopping-list aggregation rows.
      void qc.invalidateQueries({ queryKey: ['shopping-list'] })
    },
  })
}

export function useClearKitchenBrandMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { kitchenLocationId: string; ingredientId: string }) =>
      apiFetch<{ cleared: number }>('/api/kitchen-brand-selections', {
        method: 'DELETE',
        query: { kitchenLocationId: input.kitchenLocationId, ingredientId: input.ingredientId },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['kitchen-brand-selections'] })
      void qc.invalidateQueries({ queryKey: ['kitchen-orders', 'shipments'] })
      void qc.invalidateQueries({ queryKey: ['shopping-list'] })
    },
  })
}
